// PayPal Payment Service
// Uses PayPal Hosted Buttons SDK
// Documentation: https://developer.paypal.com/docs/checkout/

const PAYPAL_CONFIG = {
  clientId:
    'BAAr1GiFYDclmi5umwNgApoD75Z_N_D2U4sboFRIEt6wWECeTu9MlWiwiPipd1EQPjO-ya8NLFaIl_C-pM',
  currency: 'EUR',
  // PayPal Hosted Button ID - Get from PayPal Dashboard > Pay & Get Paid > PayPal buttons
  hostedButtonId:
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_PAYPAL_HOSTED_BUTTON_ID) ||
    'PAUCN92222FGQ',
}

interface DonationData {
  amount: number
  currency: string
  donorName: string
  donorEmail: string
  companyName?: string
}

export class PayPalService {
  private clientId: string
  private hostedButtonId: string
  private sdkLoaded: boolean = false

  constructor(clientId: string, hostedButtonId: string) {
    this.clientId = clientId
    this.hostedButtonId = hostedButtonId
  }

  isConfigured(): boolean {
    return !!this.clientId && this.clientId.length > 10
  }

  /**
   * Load PayPal SDK script
   */
  async loadSDK(): Promise<boolean> {
    if (this.sdkLoaded && (window as any).paypal) {
      return true
    }

    // Remove any existing PayPal script to avoid conflicts
    const existingScript = document.querySelector(
      'script[src*="paypal.com/sdk"]',
    )
    if (existingScript) {
      existingScript.remove()
    }

    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = `https://www.paypal.com/sdk/js?client-id=${this.clientId}&components=hosted-buttons&disable-funding=venmo&currency=${PAYPAL_CONFIG.currency}`
      script.async = true

      script.onload = () => {
        console.log('✅ PayPal SDK loaded successfully')
        this.sdkLoaded = true
        resolve(true)
      }

      script.onerror = () => {
        console.error('❌ Failed to load PayPal SDK')
        resolve(false)
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Render PayPal Hosted Button in a container
   */
  async renderHostedButton(containerId: string): Promise<boolean> {
    try {
      const loaded = await this.loadSDK()
      if (!loaded) {
        console.error('PayPal SDK not loaded')
        return false
      }

      const paypal = (window as any).paypal
      if (!paypal || !paypal.HostedButtons) {
        console.error('PayPal HostedButtons not available')
        return false
      }

      if (!this.hostedButtonId) {
        console.warn(
          '⚠️ No hosted button ID configured. Set VITE_PAYPAL_HOSTED_BUTTON_ID',
        )
        return false
      }

      // Clear container before rendering
      const container = document.getElementById(containerId)
      if (container) {
        container.innerHTML = ''
      }

      await paypal
        .HostedButtons({
          hostedButtonId: this.hostedButtonId,
        })
        .render(`#${containerId}`)

      console.log('✅ PayPal Hosted Button rendered')
      return true
    } catch (error) {
      console.error('❌ Error rendering PayPal button:', error)
      return false
    }
  }

  /**
   * Fallback: redirect to PayPal donate page
   */
  async createPayment(
    data: DonationData,
  ): Promise<{ success: boolean; error?: string }> {
    console.log('💰 PayPal payment initiated:', data)

    // If hosted button ID exists, it should be rendered inline (not redirect)
    if (this.hostedButtonId) {
      return {
        success: false,
        error: 'Utilisez le bouton PayPal affiché ci-dessous.',
      }
    }

    // Fallback: PayPal donate URL
    const params = new URLSearchParams({
      business: 'contact@adcc.africa',
      amount: data.amount.toString(),
      currency_code: data.currency.toUpperCase(),
      item_name: 'Don ADCC - Insertion professionnelle digne au Bénin',
      return: 'https://adcc.africa/donate?success=true',
      cancel_return: 'https://adcc.africa/donate?cancelled=true',
    })

    const paypalUrl = `https://www.paypal.com/donate?${params.toString()}`
    console.log('🔗 Redirecting to PayPal Donate:', paypalUrl)
    window.open(paypalUrl, '_blank')
    return { success: true }
  }
}

// Export singleton instance
export const paypalService = new PayPalService(
  PAYPAL_CONFIG.clientId,
  PAYPAL_CONFIG.hostedButtonId,
)
